<template>
  <div
    v-if="isLoadingWaiverInfo"
    class="spinner-container"
    role="status"
    :aria-label="T('Loading...')"
  >
    <FhSpinner size="lg" />
  </div>

  <template v-else>
    <SpinnerContainer v-if="isLoadingSubmit" />

    <ReachedLimit
      v-if="isReachedLimit"
      :booking="waiverInfo.booking"
      :company="waiverInfo.company"
    />

    <SignedSuccess
      v-else-if="isSignedSuccess"
      :email="clientEmail"
      :is-dock-device="props.isDockDevice"
    />

    <FhWaiverSignatureForm
      v-else
      :is-loading-submit="isLoadingSubmit"
      :booking="waiverInfo.booking"
      :company="waiverInfo.company"
      :waiver="waiverInfo.waiver"
      :company-shortname="props.companyShortname"
      :click-return-button="props.clickReturnButton"
      :is-dock-device="props.isDockDevice"
      @submit="handleSubmitForm"
    />
  </template>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { FhSpinner } from '@fareharbor-com/beacon-vue';
import FhWaiverSignatureForm from './FhWaiverSignatureForm.vue';
import ReachedLimit from './waiver-statuses/ReachedLimit.vue';
import SignedSuccess from './waiver-statuses/SignedSuccess.vue';
import SpinnerContainer from './shared/SpinnerContainer.vue';
import {
  fetchWaiver,
  signWaiver,
  fetchStandaloneWaiver,
  signStandaloneWaiver,
  redirectToRebooking,
} from './utils';
import { WAIVER_MODE } from '../constants/waiverModes';

const props = defineProps({
  mode: {
    type: String,
    default: WAIVER_MODE.BOOKING,
  },
  standaloneLinkUuid: {
    type: String,
    default: '',
  },
  companyShortname: {
    type: String,
    default: '',
  },
  bookingUuid: {
    type: String,
    default: '',
  },
  setIsNotAvailable: {
    type: Function,
    default: () => {},
  },
  isDockDevice: {
    type: Boolean,
    default: false,
  },
});

const isStandalone = props.mode === WAIVER_MODE.STANDALONE;

const isLoadingWaiverInfo = ref(true);
const isLoadingSubmit = ref(false);
const isReachedLimit = ref(false);
const isSignedSuccess = ref(false);
const clientEmail = ref('');

const waiverInfo = ref({});
const signingToken = ref('');

// Standalone submit needs the single-use signing token minted by the GET. If it has
// expired/been used (401), mint a fresh one and retry once before giving up.
const submitStandalone = async (signWaiverFormData) => {
  const result = await signStandaloneWaiver(
    props.standaloneLinkUuid,
    signingToken.value,
    signWaiverFormData,
  );

  if (!result.isTokenInvalid) {
    return result;
  }

  const refreshed = await fetchStandaloneWaiver(props.standaloneLinkUuid);

  // Prefer explicit refresh failures (404/429) over a token that may still be
  // present in a rate-limited payload — do not retry the signature POST then.
  if (refreshed.isNotFound || refreshed.isReachedLimit) {
    return refreshed;
  }

  if (refreshed.data?.signingToken) {
    signingToken.value = refreshed.data.signingToken;
    return signStandaloneWaiver(
      props.standaloneLinkUuid,
      signingToken.value,
      signWaiverFormData,
    );
  }

  return { isTokenInvalid: true };
};

const handleSubmitForm = async (signWaiverFormData) => {
  isLoadingSubmit.value = true;

  const {
    data,
    isNotFound: isNotFoundResponse,
    isReachedLimit: isReachedLimitResponse,
    isTokenInvalid: isTokenInvalidResponse,
  } = isStandalone
    ? await submitStandalone(signWaiverFormData)
    : await signWaiver(
        props.companyShortname,
        props.bookingUuid,
        signWaiverFormData,
      );

  if (isNotFoundResponse || isTokenInvalidResponse) {
    props.setIsNotAvailable();
  } else if (isReachedLimitResponse) {
    isReachedLimit.value = isReachedLimitResponse;
    waiverInfo.value = data;
  } else {
    isSignedSuccess.value = true;
    clientEmail.value = signWaiverFormData.email;
  }

  isLoadingSubmit.value = false;
};

onMounted(async () => {
  const {
    data,
    isNotFound: isNotFoundResponse,
    isReachedLimit: isReachedLimitResponse,
    isRebooked: isRebookedResponse,
  } = isStandalone
    ? await fetchStandaloneWaiver(props.standaloneLinkUuid)
    : await fetchWaiver(props.companyShortname, props.bookingUuid);

  if (isNotFoundResponse) {
    props.setIsNotAvailable();
  } else if (isReachedLimitResponse) {
    isReachedLimit.value = isReachedLimitResponse;
    waiverInfo.value = data;
  } else if (isRebookedResponse) {
    redirectToRebooking(data.redirectToRebookingUuid);
  } else {
    waiverInfo.value = data;
    signingToken.value = data.signingToken || '';
  }

  isLoadingWaiverInfo.value = false;
});
</script>

<style scoped>
.spinner-container {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
